import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { extractApiError } from '@/lib/api';
import { dossierCompanyService } from '../services/dossier-company.service';
import type {
  CreateBlockInput,
  CreateCertificationInput,
  CreateInterventionZoneInput,
  CreateOrUpdateProfileInput,
  ListBlocksFilters,
  PieceType,
  PresignPieceUploadInput,
  UpdateAggregatedReferencesInput,
  UpdateBlockInput,
  UpdateCertificationInput,
  UpdateCommercialStrategyInput,
  UpdateInterventionZoneInput,
  UpdatePieceMetadataInput,
  UpsertAnnualRevenueInput,
} from '../types/dossier-company.types';

/**
 * Sprint 5 v3 — Hooks TanStack Query pour le Dossier company.
 *
 * Convention de cache :
 *   - 1 query par brique (pieces, blocks, profile) + queries dédiées CA/certifs
 *   - Invalidation ciblée après mutation (pas de invalidateQueries global)
 *   - Toasts sonner pour feedback utilisateur (cohérent avec le reste de l'app)
 */

const KEYS = {
  pieces: (filters?: { type?: PieceType }) => ['dossier-company', 'pieces', filters] as const,
  blocks: (filters?: ListBlocksFilters) => ['dossier-company', 'blocks', filters] as const,
  block: (id: number) => ['dossier-company', 'block', id] as const,
  profile: ['dossier-company', 'profile'] as const,
  revenues: ['dossier-company', 'revenues'] as const,
  certifications: ['dossier-company', 'certifications'] as const,
  variables: ['dossier-company', 'variables'] as const,
  effectiveCpv: ['dossier-company', 'effective-cpv'] as const,
  cpvCodes: ['dossier-company', 'cpv-codes'] as const,
  interventionZones: (includeArchived = false) =>
    ['dossier-company', 'intervention-zones', includeArchived] as const,
  onboardingStatus: ['dossier-company', 'onboarding-status'] as const,
};

// ─────────────────── Pièces ───────────────────

export function usePieces(filters?: { type?: PieceType }) {
  return useQuery({
    queryKey: KEYS.pieces(filters),
    queryFn: () => dossierCompanyService.listPieces(filters),
  });
}

export function useUploadPiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PresignPieceUploadInput & { file: File }) => {
      const { file, ...meta } = input;
      const presign = await dossierCompanyService.presignPiece(meta);
      await dossierCompanyService.uploadToMinio(presign.uploadUrl, file);
      await dossierCompanyService.confirmPiece(presign.pieceId);
      return presign.pieceId;
    },
    onSuccess: () => {
      toast.success('Pièce uploadée avec succès');
      qc.invalidateQueries({ queryKey: ['dossier-company', 'pieces'] });
    },
    onError: (err: Error) => {
      toast.error(`Échec de l'upload : ${err.message}`);
    },
  });
}

export function useUpdatePiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, input }: { pieceId: number; input: UpdatePieceMetadataInput }) =>
      dossierCompanyService.updatePieceMetadata(pieceId, input),
    onSuccess: () => {
      toast.success('Pièce mise à jour');
      qc.invalidateQueries({ queryKey: ['dossier-company', 'pieces'] });
    },
  });
}

export function useDeletePiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pieceId: number) => dossierCompanyService.deletePiece(pieceId),
    onSuccess: () => {
      toast.success('Pièce archivée (purge MinIO différée à 30j)');
      qc.invalidateQueries({ queryKey: ['dossier-company', 'pieces'] });
    },
  });
}

export function useDownloadPiece() {
  return useMutation({
    mutationFn: async (pieceId: number) => {
      const url = await dossierCompanyService.getDownloadUrl(pieceId);
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  });
}

// ─────────────────── Blocs ───────────────────

export function useBlocks(filters?: ListBlocksFilters) {
  return useQuery({
    queryKey: KEYS.blocks(filters),
    queryFn: () => dossierCompanyService.listBlocks(filters),
  });
}

export function useBlock(blockId: number | null) {
  return useQuery({
    queryKey: KEYS.block(blockId ?? 0),
    queryFn: () => dossierCompanyService.getBlock(blockId as number),
    enabled: blockId !== null && blockId > 0,
  });
}

export function useCreateBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBlockInput) => dossierCompanyService.createBlock(input),
    onSuccess: () => {
      toast.success('Bloc créé');
      qc.invalidateQueries({ queryKey: ['dossier-company', 'blocks'] });
    },
  });
}

export function useUpdateBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ blockId, input }: { blockId: number; input: UpdateBlockInput }) =>
      dossierCompanyService.updateBlock(blockId, input),
    onSuccess: (_, { blockId }) => {
      toast.success('Bloc mis à jour');
      qc.invalidateQueries({ queryKey: ['dossier-company', 'blocks'] });
      qc.invalidateQueries({ queryKey: KEYS.block(blockId) });
    },
  });
}

export function useDeleteBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockId: number) => dossierCompanyService.deleteBlock(blockId),
    onSuccess: () => {
      toast.success('Bloc archivé');
      qc.invalidateQueries({ queryKey: ['dossier-company', 'blocks'] });
    },
  });
}

// ─────────────────── Profil ───────────────────

export function useProfile() {
  return useQuery({
    queryKey: KEYS.profile,
    queryFn: () => dossierCompanyService.getProfile(),
  });
}

export function useUpsertProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrUpdateProfileInput) => dossierCompanyService.updateProfile(input),
    onSuccess: () => {
      toast.success('Profil enregistré');
      qc.invalidateQueries({ queryKey: KEYS.profile });
    },
  });
}

export function useRefreshSirene() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dossierCompanyService.refreshSirene(),
    onSuccess: (result) => {
      if (result.status === 'OK') {
        toast.success('Données SIRENE rafraîchies');
      } else if (result.status === 'NOT_FOUND') {
        toast.error('SIRET introuvable côté INSEE');
      } else {
        toast.error(`Statut : ${result.status}`);
      }
      qc.invalidateQueries({ queryKey: KEYS.profile });
    },
    onError: (err: Error) => {
      toast.error(`Refresh SIRENE échoué : ${err.message}`);
    },
  });
}

// ─────────────────── CA ───────────────────

export function useRevenues() {
  return useQuery({
    queryKey: KEYS.revenues,
    queryFn: () => dossierCompanyService.listRevenues(),
  });
}

export function useUpsertRevenue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertAnnualRevenueInput) => dossierCompanyService.upsertRevenue(input),
    onSuccess: () => {
      toast.success('CA enregistré');
      qc.invalidateQueries({ queryKey: KEYS.revenues });
      qc.invalidateQueries({ queryKey: KEYS.profile });
    },
  });
}

export function useDeleteRevenue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (revenueId: number) => dossierCompanyService.deleteRevenue(revenueId),
    onSuccess: () => {
      toast.success('Ligne CA supprimée');
      qc.invalidateQueries({ queryKey: KEYS.revenues });
      qc.invalidateQueries({ queryKey: KEYS.profile });
    },
  });
}

// ─────────────────── Certifications ───────────────────

export function useCertifications() {
  return useQuery({
    queryKey: KEYS.certifications,
    queryFn: () => dossierCompanyService.listCertifications(),
  });
}

export function useCreateCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCertificationInput) => dossierCompanyService.createCertification(input),
    onSuccess: () => {
      toast.success('Certification ajoutée');
      qc.invalidateQueries({ queryKey: KEYS.certifications });
      qc.invalidateQueries({ queryKey: KEYS.profile });
    },
  });
}

export function useUpdateCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ certId, input }: { certId: number; input: UpdateCertificationInput }) =>
      dossierCompanyService.updateCertification(certId, input),
    onSuccess: () => {
      toast.success('Certification mise à jour');
      qc.invalidateQueries({ queryKey: KEYS.certifications });
      qc.invalidateQueries({ queryKey: KEYS.profile });
    },
  });
}

export function useArchiveCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (certId: number) => dossierCompanyService.archiveCertification(certId),
    onSuccess: () => {
      toast.success('Certification archivée');
      qc.invalidateQueries({ queryKey: KEYS.certifications });
      qc.invalidateQueries({ queryKey: KEYS.profile });
    },
  });
}

// ─────────────────── Variables ───────────────────

export function useAvailableVariables() {
  return useQuery({
    queryKey: KEYS.variables,
    queryFn: () => dossierCompanyService.listAvailableVariables(),
    staleTime: 60 * 60 * 1000, // 1h — la liste est statique
  });
}

// ─────────────────── Sprint 5b.1 v2 — Effective CPV ───────────────────

export function useEffectiveCpv() {
  return useQuery({
    queryKey: KEYS.effectiveCpv,
    queryFn: () => dossierCompanyService.getEffectiveCpv(),
    staleTime: 30 * 1000, // 30s — la vue dépend du NAF + extensions/exclusions
  });
}

export function useCpvCodes() {
  return useQuery({
    queryKey: KEYS.cpvCodes,
    queryFn: () => dossierCompanyService.listCpvCodes(),
    staleTime: Infinity, // référentiel quasi-statique (~9366 codes)
    gcTime: 24 * 60 * 60 * 1000, // 24h en mémoire navigateur
  });
}

// ─────────────────── Sprint 5b.1 v2 — Zones d'intervention ───────────────────

export function useInterventionZones(includeArchived = false) {
  return useQuery({
    queryKey: KEYS.interventionZones(includeArchived),
    queryFn: () => dossierCompanyService.listInterventionZones(includeArchived),
  });
}

export function useCreateInterventionZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInterventionZoneInput) =>
      dossierCompanyService.createInterventionZone(input),
    onSuccess: () => {
      toast.success('Zone d\'intervention ajoutée');
      qc.invalidateQueries({ queryKey: ['dossier-company', 'intervention-zones'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err));
    },
  });
}

export function useUpdateInterventionZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ zoneId, input }: { zoneId: number; input: UpdateInterventionZoneInput }) =>
      dossierCompanyService.updateInterventionZone(zoneId, input),
    onSuccess: () => {
      toast.success('Zone mise à jour');
      qc.invalidateQueries({ queryKey: ['dossier-company', 'intervention-zones'] });
    },
  });
}

export function useArchiveInterventionZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (zoneId: number) => dossierCompanyService.archiveInterventionZone(zoneId),
    onSuccess: () => {
      toast.success('Zone archivée');
      qc.invalidateQueries({ queryKey: ['dossier-company', 'intervention-zones'] });
    },
  });
}

// ─────────────────── Sprint 5b.1 v2 — Stratégie commerciale ───────────────────

export function useUpdateCommercialStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCommercialStrategyInput) =>
      dossierCompanyService.updateCommercialStrategy(input),
    onSuccess: () => {
      toast.success('Stratégie commerciale enregistrée');
      qc.invalidateQueries({ queryKey: KEYS.profile });
      qc.invalidateQueries({ queryKey: KEYS.effectiveCpv });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err));
    },
  });
}

export function useUpdateAggregatedReferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAggregatedReferencesInput) =>
      dossierCompanyService.updateAggregatedReferences(input),
    onSuccess: () => {
      toast.success('Références enregistrées');
      qc.invalidateQueries({ queryKey: KEYS.profile });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err));
    },
  });
}

// ─────────────────── Sprint 5b.1 v2 — Wizard onboarding ───────────────────

export function useDismissOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dossierCompanyService.dismissOnboarding(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.onboardingStatus });
    },
  });
}

export function useOnboardingStatus() {
  return useQuery({
    queryKey: KEYS.onboardingStatus,
    queryFn: () => dossierCompanyService.getOnboardingStatus(),
    staleTime: 60 * 1000, // 1 min — change rarement
  });
}
